import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { useState } from "react";
import { Plus, ArrowDownToLine, LayoutGrid, Users, GitBranch } from "lucide-react";
import { useApps } from "@/features/apps/hooks/useApps";
import { CreateAppDialog } from "@/features/apps/components/CreateAppDialog";
import { ImportCodepushDialog } from "@/features/apps/components/ImportCodepushDialog";
import { PendingTransfersSection } from "@/features/apps/components/PendingTransfersSection";
import { ApiErrorBanner } from "@/components/ui/ApiErrorBanner";
import { PageLoader } from "@/components/ui/PageLoader";

export const Route = createFileRoute("/_authenticated/apps/")({
  component: AppsPage,
});

function AppsPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data, isLoading, error } = useApps();
  const [createOpen, setCreateOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);

  const apps = data?.apps ?? [];

  return (
    <div className="mx-auto max-w-5xl">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-heading">{t("apps.title")}</h1>
          <p className="mt-1 text-sm text-muted">{t("apps.subtitle")}</p>
        </div>
        {apps.length > 0 && (
          <div className="flex flex-wrap items-center justify-end gap-3">
            <button
              type="button"
              onClick={() => setImportOpen(true)}
              className="inline-flex items-center gap-2 rounded-lg border border-border bg-surface px-4 py-2.5 text-sm font-medium text-muted transition-colors hover:border-border-light hover:text-heading"
            >
              <ArrowDownToLine size={16} />
              {t("apps.importCodepush")}
            </button>
            <button
              type="button"
              onClick={() => setCreateOpen(true)}
              className="inline-flex items-center gap-2 rounded-lg bg-indigo px-4 py-2.5 text-sm font-semibold text-white transition-all hover:bg-indigo-bright hover:shadow-lg hover:shadow-indigo/25 active:scale-[0.98]"
            >
              <Plus size={16} />
              {t("apps.createApp")}
            </button>
          </div>
        )}
      </div>

      {isLoading && <PageLoader />}

      <ApiErrorBanner error={error} className="mt-8" />

      <PendingTransfersSection />

      {!isLoading && !error && apps.length === 0 && (
        <div className="mt-16 flex flex-col items-center gap-6 text-center">
          <div className="flex h-20 w-20 items-center justify-center rounded-2xl bg-indigo/10">
            <LayoutGrid size={36} className="text-indigo" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-heading">{t("apps.emptyTitle")}</h2>
            <p className="mt-1 max-w-sm text-sm text-muted">{t("apps.emptySubtitle")}</p>
          </div>
          <div className="flex flex-wrap items-center justify-center gap-3">
            <button
              type="button"
              onClick={() => setCreateOpen(true)}
              className="inline-flex items-center gap-2 rounded-lg bg-indigo px-5 py-2.5 text-sm font-semibold text-white transition-all hover:bg-indigo-bright hover:shadow-lg hover:shadow-indigo/25 active:scale-[0.98]"
            >
              <Plus size={16} />
              {t("apps.createApp")}
            </button>
            <button
              type="button"
              onClick={() => setImportOpen(true)}
              className="inline-flex items-center gap-2 rounded-lg border border-border bg-surface px-5 py-2.5 text-sm font-medium text-muted transition-colors hover:border-border-light hover:text-heading"
            >
              <ArrowDownToLine size={16} />
              {t("apps.importCodepush")}
            </button>
          </div>
        </div>
      )}

      {!isLoading && !error && apps.length > 0 && (
        <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {apps.map((app) => (
            <Link
              key={app.name}
              to="/apps/$appName"
              params={{ appName: app.name }}
              className="group rounded-xl border border-border bg-surface p-5 transition-all hover:border-indigo/30 hover:shadow-md hover:shadow-indigo/5"
            >
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-gradient-to-br from-indigo/20 to-cyan/20 text-indigo transition-colors group-hover:from-indigo/30 group-hover:to-cyan/30">
                  <LayoutGrid size={18} />
                </div>
                <h3 className="text-sm font-semibold text-heading truncate">{app.name}</h3>
              </div>

              <div className="mt-4 flex items-center gap-4 text-xs text-muted">
                <span className="inline-flex items-center gap-1">
                  <GitBranch size={12} />
                  {app.deployments?.length ?? 0} {t("apps.deployments").toLowerCase()}
                </span>
                {app.collaborators && (
                  <span className="inline-flex items-center gap-1">
                    <Users size={12} />
                    {Object.keys(app.collaborators).length} {t("apps.collaborators").toLowerCase()}
                  </span>
                )}
              </div>
            </Link>
          ))}
        </div>
      )}

      <CreateAppDialog open={createOpen} onClose={() => setCreateOpen(false)} />
      <ImportCodepushDialog
        open={importOpen}
        onClose={() => setImportOpen(false)}
        onViewApp={(appName) => void navigate({ to: "/apps/$appName", params: { appName } })}
      />
    </div>
  );
}
